import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  CardBody,
  Typography,
  Spinner,
  Alert,
  IconButton,
  Tooltip,
  Chip,
} from '@material-tailwind/react';
import {
  ArrowPathIcon,
  ShareIcon,
  EyeIcon,
  LinkIcon,
} from '@heroicons/react/24/outline';

import { listReportsForDraft, getShare, getShareAnalytics, getReportPreview } from '../../services/reportApi';
import ShareDialog from '../ShareDialog';
import { friendlyTimeFormatDt } from '../../utils';

// Format active engagement time -
function formatActiveTime(seconds) {
  if (!seconds || seconds === 0) return null;
  if (seconds < 60) return `${seconds}s`;
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return secs > 0 ? `${mins}m ${secs}s` : `${mins}m`;
}

function getEngagementColor(seconds) {
  if (!seconds || seconds < 60) return 'text-gray-400';
  if (seconds < 180) return 'text-blue-500';
  return 'text-green-600';
}

function ReportsPanel({ draftId, draft, refreshKey }) {
  const [reports, setReports] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [accessDenied, setAccessDenied] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [shareData, setShareData] = useState({});

  const loadShareData = useCallback(async (reportsList) => {
    const reportsWithShares = reportsList.filter((r) => r.shareId);
    if (reportsWithShares.length === 0) return;

    const shareInfo = {};
    await Promise.all(
      reportsWithShares.map(async (report) => {
        const reportId = report.reportId || report.id;
        try {
          const [shareResult, analyticsResult] = await Promise.allSettled([
            getShare(reportId),
            getShareAnalytics(reportId),
          ]);

          const shareObj = shareResult.status === 'fulfilled' ? shareResult.value.share : null;
          const analytics = analyticsResult.status === 'fulfilled' ? analyticsResult.value : null;

          // Aggregate across all shares from analytics endpoint
          const shares = analytics?.shares || [];
          const totalViews = shares.reduce((sum, s) => sum + (s.totalViews || 0), 0);
          const hasActive = shares.some((s) => s.isActive) || shareObj?.isActive;

          // Find the most recent view timestamp across all shares
          const allViews = shares.flatMap((s) => s.recentViews || []);
          const lastViewedAt = allViews.length > 0
            ? allViews.reduce((latest, v) => {
                const t = new Date(v.timestamp);
                return t > latest ? t : latest;
              }, new Date(0)).toISOString()
            : shareObj?.analytics?.lastViewedAt || null;

          shareInfo[reportId] = {
            ...(shareObj || {}),
            isActive: hasActive || false,
            totalViews,
            lastViewedAt,
          };
        } catch (err) {
          // Silently fail for individual share fetches
        }
      })
    );
    setShareData((prev) => ({ ...prev, ...shareInfo }));
  }, []);

  const loadReports = useCallback(async () => {
    if (!draftId) return;
    try {
      setIsLoading(true);
      setError(null);
      setAccessDenied(false);
      // Draft-scoped backend route: it authorizes by draft access (owner, team,
      // delegate, or admin), so a delegate viewing their agent's draft gets its
      // versions. Runs server-side with the Admin SDK — no client Firestore rules.
      const data = await listReportsForDraft(draftId);
      const draftReports = (
        Array.isArray(data) ? data : Array.isArray(data?.reports) ? data.reports : []
      )
        .slice()
        // Oldest first so v1 is first.
        .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
      setReports(draftReports);

      if (draftReports.length > 0) {
        loadShareData(draftReports);
      }
    } catch (err) {
      console.error('Failed to load reports:', err);
      setReports([]);
      // 403 = viewer can't access this draft (not owner/delegate/team/admin).
      // Show a calm message rather than a raw error.
      if (err?.status === 403) {
        setAccessDenied(true);
      } else {
        setError(err.message);
      }
    } finally {
      setIsLoading(false);
    }
  }, [draftId, loadShareData]);

  useEffect(() => {
    loadReports();
  }, [loadReports, refreshKey]);

  const handleShare = (report) => {
    setSelectedReport(report);
    setShareDialogOpen(true);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <Card className="h-full">
      <CardBody className="p-4">
        <div className="flex items-center justify-between mb-4">
          <Typography variant="h6" color="blue-gray">
            Report Versions
          </Typography>
          <Tooltip content="Refresh">
            <IconButton variant="text" size="sm" onClick={loadReports} disabled={isLoading}>
              <ArrowPathIcon className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
            </IconButton>
          </Tooltip>
        </div>

        {error && (
          <Alert color="red" className="mb-4" onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Spinner className="h-8 w-8" />
          </div>
        ) : accessDenied ? (
          <div className="flex flex-col items-center justify-center py-12 text-gray-500">
            <Typography>Versions not available</Typography>
            <Typography variant="small" color="gray" className="mt-1">
              You don't have access to this draft's report versions.
            </Typography>
          </div>
        ) : reports.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-gray-500">
            <Typography>No reports generated yet</Typography>
            <Typography variant="small" color="gray" className="mt-1">
              Click "Create Version" to generate your first version.
            </Typography>
          </div>
        ) : (
          <div className="space-y-3">
            {reports.map((report, index) => {
              const reportId = report.reportId || report.id;
              const version = report.version || 1;
              const share = shareData[reportId];
              const viewCount = share?.totalViews || share?.analytics?.viewCount || 0;
              const activeTime = share?.analytics?.totalActiveSeconds;
              const lastViewed = share?.lastViewedAt || share?.analytics?.lastViewedAt;

              return (
                <div
                  key={reportId}
                  className="p-3 border border-gray-200 rounded-lg hover:border-blue-300 hover:bg-gray-50 transition-colors"
                >
                  {/* Header row: Version + Status + Actions */}
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Chip
                        size="sm"
                        value={`v${version}`}
                        color="green"
                        variant="ghost"
                      />
                      {share?.isActive ? (
                        <div className="flex items-center gap-1">
                          <LinkIcon className="h-3.5 w-3.5 text-green-500" />
                          <Typography variant="small" className="text-xs font-medium text-green-600">
                            Active
                          </Typography>
                        </div>
                      ) : share ? (
                        <Typography variant="small" className="text-xs text-gray-400">
                          Inactive
                        </Typography>
                      ) : null}
                    </div>
                    <div className="flex items-center gap-1">
                      <Tooltip content="Preview">
                        <IconButton
                          variant="text"
                          size="sm"
                          onClick={async () => {
                            try {
                              const html = await getReportPreview(reportId);
                              const blob = new Blob([html], { type: 'text/html' });
                              window.open(URL.createObjectURL(blob), '_blank');
                            } catch (err) {
                              console.error('Preview error:', err);
                              setError(err.message);
                            }
                          }}
                        >
                          <EyeIcon className="h-4 w-4" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip content="Share">
                        <IconButton
                          variant="text"
                          size="sm"
                          onClick={() => handleShare(report)}
                        >
                          <ShareIcon className="h-4 w-4" />
                        </IconButton>
                      </Tooltip>
                    </div>
                  </div>

                  {/* Details row */}
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-500">
                    <span>Created {formatDate(report.createdAt)}</span>
                    {viewCount > 0 ? (
                      <div className="flex items-center gap-1">
                        <EyeIcon className="h-3.5 w-3.5 text-green-500" />
                        <span className="font-medium text-green-600">
                          {viewCount} {viewCount === 1 ? 'view' : 'views'}
                        </span>
                        {activeTime > 0 && (
                          <span className={getEngagementColor(activeTime)}>
                            ({formatActiveTime(activeTime)})
                          </span>
                        )}
                      </div>
                    ) : share ? (
                      <span className="text-gray-400">Not viewed</span>
                    ) : null}
                    {lastViewed && (
                      <span>Last viewed {friendlyTimeFormatDt(lastViewed)}</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardBody>

      {/* Share Dialog */}
      <ShareDialog
        open={shareDialogOpen}
        onClose={() => {
          setShareDialogOpen(false);
          if (selectedReport) {
            const reportId = selectedReport.reportId || selectedReport.id;
            Promise.allSettled([getShare(reportId), getShareAnalytics(reportId)])
              .then(([shareResult, analyticsResult]) => {
                const shareObj = shareResult.status === 'fulfilled' ? shareResult.value.share : null;
                const analytics = analyticsResult.status === 'fulfilled' ? analyticsResult.value : null;
                const shares = analytics?.shares || [];
                const totalViews = shares.reduce((sum, s) => sum + (s.totalViews || 0), 0);
                const hasActive = shares.some((s) => s.isActive) || shareObj?.isActive;
                const allViews = shares.flatMap((s) => s.recentViews || []);
                const lastViewedAt = allViews.length > 0
                  ? allViews.reduce((latest, v) => {
                      const t = new Date(v.timestamp);
                      return t > latest ? t : latest;
                    }, new Date(0)).toISOString()
                  : shareObj?.analytics?.lastViewedAt || null;
                setShareData((prev) => ({
                  ...prev,
                  [reportId]: { ...(shareObj || {}), isActive: hasActive || false, totalViews, lastViewedAt },
                }));
              })
              .catch(() => {});
          }
          setSelectedReport(null);
        }}
        reportId={selectedReport?.reportId || selectedReport?.id}
        reportTitle={`${draft?.customerName || ''} - ${draft?.aircraftModel || ''}`}
        reportType={draft?.reportType}
      />
    </Card>
  );
}

export default ReportsPanel;
